<script setup>
    import {computed, ref} from "vue";
    import {globalStore} from "@pinia/global-store";
    import {appendLangToLinks, formatProtocolText, objNotEmpty, isSvgImage} from "@util/mixins";

    import Image from "@component/Image/Image.vue";
    import SVG from "@component/Image/SVG.vue";
    import {isString} from "lodash";

    const props = defineProps({
        trial: Object,
        isSubTrial: {
            type: Boolean,
            default: false,
        }
    })

    const {
        id,
        age,
        condition,
        distance,
        image,
        mkId,
        numOfLocations,
        permalink,
        phase,
        postType,
        sex,
        status,
        statusType,
        studyCount,
        studyProtocol,
        subStudiesList,
        title,
        trialId,
        umbrellaProtocol,
    } = props.trial;

    const {trialLabels} = LABELS;
    const {
        location,
        noDistance,
        numberOfLocations,
        numberOfStudies,
        sexesStudied,
        subStudiesLabel,
        units
    } = trialLabels
    const viewport = computed(() => globalStore().viewport);
    const subStudyText = ref('')

    subStudyText.value = formatProtocolText(
        props.isSubTrial,
        umbrellaProtocol,
        subStudiesList,
        subStudiesLabel
    )

    const distanceOrCountLabel = () => location;

    /**
     * Display the distance as a number or if there is none, the appropriate
     * return statement for no distance
     *
     * @returns {*|{length}|string}
     */
    const distanceOrCount = () => {
        if (isString(distance)) {
            if (distance.length) return distance
            return noDistance
        }

        return `${distance} ${units}`
    }

</script>

<template>
    <div
        :id="id"
        class="trial-result trial-card"
        :class="{ 'umbrella-protocol': umbrellaProtocol }"
    >
        <div class="column">
            <div class="top">
                <div class="">
                    <p
                        class="label"
                        v-html="trialLabels.status"
                    />
                    <div
                        v-if="statusType && status"
                        class="status"
                        :class="statusType"
                    >
                        <p v-html="status"/>
                    </div>
                </div>

                <div class="trial-name">

                    <p
                        class="label"
                        v-html="postType === 'trials' ? trialLabels.name : trialLabels.nameProgram"
                    />

                    <!-- Post Title -->
                    <h3
                        v-if="title"
                        class="post-title"
                    >
                        <a
                            v-if="permalink"
                            :href="appendLangToLinks(permalink)"
                            v-html="title"
                        />
                        <span
                            v-else
                            v-html="title"
                        />
                    </h3>

                    <p
                        v-if="subStudyText"
                        class="gray-label"
                        v-html="`${isSubTrial ? trialLabels.umbrellaProtocol : ''} ${subStudyText}`"
                    />

                </div>
            </div>
            <div class="bottom">
                <div class="age">
                    <p
                        class="label"
                        v-html="trialLabels?.age || 'Age'"
                    />
                    <p
                        v-if="age"
                        v-html="age"
                    />

                </div>
                <div class="number-of-locations">
                    <p
                        class="label"
                        v-html="numberOfLocations"
                    />
                    <p v-html="numOfLocations" />
                </div>
                <div class="sexes">
                    <p
                        class="label"
                        v-html="sexesStudied"
                    />
                    <p
                        v-if="sex"
                        v-html="sex"
                    />
                </div>
                <div class="location">
                    <p
                        class="label"
                        v-html="distanceOrCountLabel()"
                    />
                    <p v-html="distanceOrCount()"/>
                </div>
                <div class="condition">
                    <p
                        class="label"
                        v-html="trialLabels.condition"
                    />
                    <p
                        v-if="condition"
                        v-html="condition"
                    />
                </div>
            </div>
        </div>
        <div class="aside">
            <div class="trial_id">
                <div
                    v-if="mkId"
                    class="trial_id_containers"
                >
                    <p
                        class="label"
                        v-html="trialLabels.mkId"
                    />
                    <p v-html="mkId"/>
                </div>

                <div
                    v-if="trialId"
                    class="trial_id_containers"
                >
                    <p
                        class="label"
                        v-html="trialLabels.trialId"
                    />
                    <p v-html="trialId"/>
                </div>
            </div>

            <div class="bottom">
                <div
                    v-if="image && objNotEmpty(image)"
                    class="trial_id_logos"
                >
                    <SVG
                        v-if="isSvgImage(image)"
                        :image="image"
                    />

                    <Image
                        v-else
                        :image="image"
                    />
                </div>

                <div
                    v-if="permalink"
                    class="button"
                >
                    <a
                        :href="permalink"
                        class="btn-primary"
                        v-html="trialLabels.ctaText"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: "Trial Card"
    }
</script>

<style scoped lang="scss"></style>
